import * as React from 'react';

import styled from '@emotion/styled';
import { Alert, Link } from '@mui/material';
import * as moment from 'moment';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation, withTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import type InvoiceAdjustment from '~/types/InvoiceAdjustment';

import { fetchInvoiceAdjustmentsOfInvoice } from '~/actions/invoice-adjustments.actions';
import * as blackLogo from '~/assets/logo/shippo-lockup-black-web.png';
import { ErrorMessage } from '~/components/MessageBox/Message';
import { GreenSpinner } from '~/components/Spinner';
import { contactLink } from '~/constants/links';
import { getDisplayInvoiceAdjustments } from '~/selectors/invoice-adjustments.selector';
import { getIsGermanShippoAddressInvoice, getUserAddress } from '~/selectors/user.selectors';
import { colors, fontWeight } from '~/styles/constants';
import { handleRequestable } from '~/utils/fetchable';

import { useFetchInvoiceDetails } from '../hooks/useFetchInvoiceDetails';
import { useFetchInvoiceItems } from '../hooks/useFetchInvoiceItems';
import InvoiceItemsTable from './InvoiceItemsTable';
import ShippoInvoiceAddress from './ShippoInvoiceAddress';

const Container = styled.div`
  margin: 1.25rem 2.5rem 2.5rem;
  width: 700px;
  box-sizing: border-box;
  font-size: 0.8125rem;
  color: ${colors.black};
`;

const InvoiceInfoSection = styled.section`
  display: grid;
  grid-template-columns: 60% 40%;
  grid-template-rows: max-content max-content;
  grid-row-gap: 2.5rem;
`;

const InvoiceItemsSectionTitle = styled.h1`
  font-size: 1.25rem;
  margin: 1.25rem 0 0;
  padding-bottom: 0.625rem;
  font-weight: ${fontWeight.normal};
  line-height: 1.1;
  color: ${colors.black};
  border-bottom: 1px solid ${colors.containerBorderColor};
`;

const InvoiceItemsSection = styled.section`
  box-sizing: border-box;
  margin: 2.5rem 0;
  font-size: 0.875rem;
`;

interface ParamTypes {
  invoiceId: string;
}

const InvoiceDisplay = (): JSX.Element => {
  const { t } = useTranslation('settings');
  const { invoiceId } = useParams<ParamTypes>();
  const isGermanShippoInvoice = useSelector(getIsGermanShippoAddressInvoice);
  const userAddress = useSelector(getUserAddress);
  const invoiceDetailsQuery = useFetchInvoiceDetails(invoiceId);
  const invoiceItemsQuery = useFetchInvoiceItems(invoiceId);
  const displayInvoiceAdjustments = useSelector(getDisplayInvoiceAdjustments);
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(fetchInvoiceAdjustmentsOfInvoice(invoiceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const renderShippoAddress = React.useCallback(
    () => (
      <section className="text-right">
        <br />
        <ShippoInvoiceAddress isGerman={isGermanShippoInvoice} />
      </section>
    ),
    [isGermanShippoInvoice],
  );

  const renderUserAddress = React.useCallback(
    () =>
      userAddress ? (
        <section>
          <strong>{userAddress.name}</strong>
          <br />
          {userAddress.company}
          <br />
          {userAddress.street1} {userAddress.streetNumber}
          <br />
          {userAddress.street2 && (
            <>
              {userAddress.street2}
              <br />
            </>
          )}
          {userAddress.city && `${userAddress.city}, `}
          {userAddress.state && `${userAddress.state}, `}
          {userAddress.zip}
          <br />
          {userAddress.country?.iso2}
        </section>
      ) : null,
    [userAddress],
  );

  const renderErrorMessage = (errorMessage: string) => (
    <ErrorMessage withIcon>{errorMessage}</ErrorMessage>
  );

  const renderInvoiceInfo = React.useCallback(
    () => {
      if (invoiceDetailsQuery.isLoading) {
        return (
          <div className="justify-self-end">
            <GreenSpinner />
          </div>
        );
      }

      return (
        <section className="text-right">
          <strong>
            {t('account.billing.invoices.download.invoiceNumber', {
              invoiceNumber: invoiceDetailsQuery.data?.data.number,
            })}
          </strong>
          <br />
          {t('account.billing.invoices.download.invoiceDate', {
            invoiceDate: moment.utc(invoiceDetailsQuery.data?.data.object_created).format('LL'),
          })}
          <br />
        </section>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoiceDetailsQuery.data?.data],
  );

  const renderInvoiceItemsTable = React.useCallback(() => {
    const renderInvoiceAdjustments = handleRequestable<InvoiceAdjustment[], string>(
      () => {},
      () => null,
      (invoiceAdjustments) => invoiceAdjustments,
      () => null,
    )(displayInvoiceAdjustments);

    if (
      invoiceDetailsQuery.isLoading ||
      invoiceItemsQuery.isLoading ||
      displayInvoiceAdjustments.kind === 'requesting'
    ) {
      return (
        <div className="flex justify-center mt-4">
          <GreenSpinner />
        </div>
      );
    }

    if (
      invoiceDetailsQuery.error ||
      invoiceItemsQuery.error ||
      displayInvoiceAdjustments.kind === 'failed' ||
      displayInvoiceAdjustments.kind === 'not-requested'
    )
      return renderErrorMessage(t('account.billing.invoices.download.errors.retrieveInvoice'));

    return (
      <InvoiceItemsTable
        invoice={invoiceDetailsQuery.data?.data}
        invoiceAdjustments={renderInvoiceAdjustments}
        invoiceItems={invoiceItemsQuery.data?.data.results}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayInvoiceAdjustments,
    invoiceDetailsQuery.data?.data,
    invoiceItemsQuery.data?.data.results,
  ]);

  const overflowAlert =
    (invoiceItemsQuery.data?.data.count ?? 0) > 200 ? (
      <Alert severity="warning" sx={{ borderRadius: 0 }}>
        <div>
          <Trans
            components={[
              <Link color="secondary" href={contactLink} key={0} target="_blank">
                0
              </Link>,
            ]}
            i18nKey="settings:account.billing.invoices.firstTwoHundredItems"
          />
        </div>
      </Alert>
    ) : null;

  return (
    <Container>
      <Helmet>
        <title>{t('account.billing.invoices.header')} - Shippo</title>
      </Helmet>
      {invoiceDetailsQuery.error ? (
        renderErrorMessage(t('account.billing.invoices.download.errors.retrieveInvoice'))
      ) : (
        <React.Fragment>
          <InvoiceInfoSection>
            <img alt="Shippo logo" className="self-center" src={blackLogo} width="160" />
            {renderShippoAddress()}
            {renderUserAddress()}
            {renderInvoiceInfo()}
          </InvoiceInfoSection>
          <InvoiceItemsSection>
            <InvoiceItemsSectionTitle>
              {t('account.billing.invoices.download.invoiceItemsTable.title')}
            </InvoiceItemsSectionTitle>
            {overflowAlert}
            {renderInvoiceItemsTable()}
          </InvoiceItemsSection>
        </React.Fragment>
      )}
    </Container>
  );
};
const ComposedInvoiceDisplay = withTranslation('settings')(InvoiceDisplay);

export default ComposedInvoiceDisplay;
